import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  Account,
  ChangeLog,
  DocumentComment,
  Pagination,
  Response,
} from '@ui/src';
import {
  Balance,
  SupplierCustomersProps,
  TotalRiskLevelValues,
  CustomerProps,
} from '../../../common/types';
import { CorporateGeneralInfo } from '../../types';
import { corporateCustomersApi } from '../services/corporate';
import { customersComponentApi } from '../../../customers/store/services/customers';
import { LinkedClient } from '../../../common/linked-clients/types';
import { FeeItem } from '../../../common/tariff/types';

const totalRiskLevelValuesMock: TotalRiskLevelValues = {
  good: [0, 24],
  medium: [25, 49],
};

export interface CorporateCustomerGeneralInfoState {
  isLoading?: boolean;
  generalInformation?: CorporateGeneralInfo;
  baseInformation?: Partial<CorporateGeneralInfo>;
  baseInformationIsLoading?: boolean;
  customerId?: string;
  chatMessagesCount?: number;
  totalRiskLevels?: TotalRiskLevelValues;
  balances?: Balance[];
  totalBalance?: {
    balance: number;
    currency: string[];
  };
  businessPartners?: {
    suppliers?: SupplierCustomersProps[];
    customers?: SupplierCustomersProps[];
  };
  mainChat?: {
    id: string;
  };
  changeLogs?: ChangeLog[];
  changeLogsPagination?: Pagination;
  customerComments?: DocumentComment[];
  customerCommentsPagination?: Pagination;
}

const initialState: CorporateCustomerGeneralInfoState = {
  isLoading: true,
  generalInformation: undefined,
  baseInformation: undefined,
  baseInformationIsLoading: false,
  customerId: undefined,
  chatMessagesCount: undefined,
  totalRiskLevels: totalRiskLevelValuesMock,
};

export const CorporateCustomerGeneralInfoSlice = createSlice({
  name: 'corporateCustomerInfo',
  initialState,
  reducers: {
    setBalances: (
      state,
      action: PayloadAction<{
        balances: Balance[];
        totalBCBalances?: number[];
      }>,
    ) => {
      state.balances = action.payload.balances;

      let balance = 0;

      const currencies: string[] = [];

      action.payload.balances.forEach((item) => {
        if (!currencies.find((currency) => currency === item.currencyId)) {
          currencies.push(item.currencyId);
        }
      });

      action?.payload?.totalBCBalances?.forEach((item) => {
        balance += item;
      });

      state.totalBalance = {
        balance,
        currency: currencies,
      };
    },
    clearGeneralState: (state) => {
      state.generalInformation = undefined;
      state.isLoading = true;
      state.balances = undefined;
      state.totalBalance = undefined;
      state.baseInformation = undefined;
      state.businessPartners = undefined;
      state.customerId = undefined;
      state.mainChat = undefined;
      state.changeLogs = undefined;
      state.changeLogsPagination = undefined;
      state.customerComments = undefined;
      state.customerCommentsPagination = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      corporateCustomersApi.endpoints.updateCorporateInfo.matchFulfilled,
      (state, action: PayloadAction<{ data: CustomerProps }>) => {
        const { data } = action.payload;

        state.baseInformation = {
          ...state.baseInformation,
          name: data.companyName,
          amlCtfComplianceProgram: data?.amlCtfComplianceProgram,
          purposeOfAccount: data?.purposeOfAccount,
          expectedTypeOfClients: data?.expectedTypeOfClients,
          requiredCurrenciesCryptoId: data?.requiredCurrenciesCryptoId,
        };
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.changeBalanceStatus.matchFulfilled,
      (state, action: any & PayloadAction<{ data: Balance }>) => {
        if (state.baseInformation && state.balances) {
          let nextBalances = ([] as Balance[]).concat(state.balances);

          if (action.meta.arg.originalArgs.body.active) {
            nextBalances.push(action.payload.data);
          } else if (
            nextBalances.find((item) => item.id === action.payload.data.id)
          ) {
            nextBalances = nextBalances.filter(
              (item) => item.id !== action.payload.data.id,
            );
          }

          state.balances = nextBalances;
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomersChangeLogs.matchFulfilled,
      (
        state,
        action: PayloadAction<Response<ChangeLog>> & {
          meta: { arg: { originalArgs: string } };
        },
      ) => {
        if (!action.meta.arg.originalArgs.includes('cursor')) {
          state.changeLogs = action.payload.data?.edges?.map(
            (item) => item.node,
          );
        } else {
          const nextChangeLogs = ([] as ChangeLog[]).concat(
            state.changeLogs as ChangeLog[],
          );

          action.payload.data.edges?.forEach((item) => {
            nextChangeLogs.push(item.node);
          });

          state.changeLogs = nextChangeLogs;
        }

        state.changeLogsPagination = action.payload.data?.pageInfo;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.bindExternalOrganization.matchFulfilled,
      (state, action: PayloadAction<{ data: LinkedClient }>) => {
        if (state.baseInformation) {
          state.baseInformation = {
            ...state.baseInformation,
            pspIntegrationOrganization: action.payload.data,
          };
        }
      },
    );
    builder.addMatcher(
      corporateCustomersApi.endpoints.getCorporateUser.matchFulfilled,
      (state, action: PayloadAction<{ data: CustomerProps }>) => {
        const { data } = action.payload;

        let totalBalance = 0;

        state.customerId = action.payload.data.id;

        data.accounts.edges.forEach((item) => {
          totalBalance += item.node.totalBalanceBC as number;
        });

        const fees: FeeItem[] = [];

        data.customFees?.forEach((item) => {
          fees.push({
            ...item,
            currency: 'GBP',
            writeOffValue: '-',
            isModified: true,
          });
        });

        data.tariff?.fees.forEach((item) => {
          if (!fees.find((fee) => fee.feeId === item.feeId)) {
            fees.push({
              ...item,
              currency: 'GBP',
              writeOffValue: '-',
            });
          }
        });

        // TODO: get riskScore
        let dataRiskProfile = { totalRiskScore: 100 };

        if (data.clientsScoreResult) {
          dataRiskProfile = {
            totalRiskScore: data.clientsScoreResult?.totalRiskScore,
          };
        }

        let body = {
          id: data.id,
          name: data.companyName,
          applicationId: data?.application?.id,
          registrationNumber: data.companyNumber,
          owner: {
            firstName: '-',
            lastName: '',
          },
          sic: data.sicCodes.join(', '),
          countryOfIncorporated: data.countryOfIncorporationId,
          accounts: data.accounts.edges.map((item) => item.node),
          statusChangeReason: data?.statusChangeReason,
          totalBalance,
          currency: 'GBP',
          clientScheme: data.clientScheme,
          currencies: [],
          status: data.status,
          cards: [
            'MC debit *8478',
            'MC corp *8788',
            'MC corp *8781',
            'MC corp *8488',
          ],
          risk: dataRiskProfile.totalRiskScore,
          tariff: data.tariff ? data.tariff.name : 'No Tariff',
          tariffId: data?.tariff?.id,
          tariffRiskScoreId: data?.tariff?.riskScoreRangeId,
          defaultFees: data?.tariff?.fees.map((item) => ({
            ...item,
            currency: '-',
            writeOffValue: '-',
          })),
          monthlyMaxValueIncomingPaymentRangeId:
            data.monthlyMaxValueIncomingPaymentRangeId,
          monthlyMaxValueOutgoingPaymentRangeId:
            data.monthlyMaxValueOutgoingPaymentRangeId,
          monthlyNumberIncomingTransactionsRangeId:
            data.monthlyNumberIncomingTransactionsRangeId,
          monthlyNumberOutgoingTransactionsRangeId:
            data.monthlyNumberOutgoingTransactionsRangeId,
          monthlyTurnoverRangeId: data.monthlyTurnoverRangeId,
          monthlyValueIncomingTransactionsRangeId:
            data.monthlyValueIncomingTransactionsRangeId,
          monthlyValueOutgoingTransactionsRangeId:
            data.monthlyValueOutgoingTransactionsRangeId,
          fees,
          officials: data.officials?.edges.map((item) => item.node),
          merchantId: data?.merchantId,
          vatNumber: data?.vatNumber || '',
          questionnaire: {
            ...data?.questionnaire,
          },
          amlCtfComplianceProgram: data?.amlCtfComplianceProgram,
          purposeOfAccount: data?.purposeOfAccount,
          expectedTypeOfClients: data?.expectedTypeOfClients,
          requiredCurrenciesCryptoId: data?.requiredCurrenciesCryptoId,
          pspIntegrationOrganization: data?.pspIntegrationOrganization,
          type: data?.type,
        };

        if (data?.clientScheme?.id === 'bcc01059-d764-40a1-8678-cdd000156445') {
          let nextSourcesOfAnnualIncomes: {
            sourceOfAnnualIncomeId: string;
            percentage?: string | number;
          }[] = [];

          if (data?.questionnaire?.sourcesOfAnnualIncomes) {
            if (
              typeof data?.questionnaire?.sourcesOfAnnualIncomes === 'string'
            ) {
              nextSourcesOfAnnualIncomes = JSON.parse(
                data?.questionnaire?.sourcesOfAnnualIncomes,
              ) as {
                sourceOfAnnualIncomeId: string;
                percentage?: string | number;
              }[];
            } else if (
              Array.isArray(data?.questionnaire?.sourcesOfAnnualIncomes)
            ) {
              nextSourcesOfAnnualIncomes =
                data?.questionnaire?.sourcesOfAnnualIncomes;
            }
          }
          body = {
            ...body,
            questionnaire: {
              ...data?.questionnaire,
              sourcesOfAnnualIncomes: nextSourcesOfAnnualIncomes,
            },
          };
        }

        state.baseInformation = {
          ...body,
        };

        state.baseInformationIsLoading = false;

        state.businessPartners = {
          suppliers: data.suppliers,
          customers: data.customers,
        };
      },
    );
    builder.addMatcher(
      corporateCustomersApi.endpoints.getCorporateUser.matchRejected,
      (state) => {
        state.baseInformationIsLoading = false;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.createCustomFee.matchFulfilled,
      (state, action: PayloadAction<{ data: FeeItem }>) => {
        const { data } = action.payload;
        if (state.baseInformation) {
          const nextFees = state.baseInformation?.fees?.map((item) => {
            let result = { ...item };

            if (result.feeId === data?.feeId) {
              result = {
                ...data,
                currency: 'GBP',
                writeOffValue: '-',
                isModified: true,
              };
            }

            return result;
          });

          state.baseInformation = {
            ...state.baseInformation,
            fees: nextFees,
          };
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.deleteCustomFee.matchFulfilled,
      (state, action: PayloadAction<{ data: FeeItem }>) => {
        if (state.baseInformation) {
          const defaultFee = state.baseInformation?.defaultFees?.find(
            (item) => item.feeId === action.payload.data.feeId,
          );

          const nextFees = state.baseInformation?.fees?.map((item) => {
            let result = { ...item };

            if (result.feeId === defaultFee?.feeId) {
              result = defaultFee;
            }

            return result;
          });

          state.baseInformation = {
            ...state.baseInformation,
            fees: nextFees,
          };
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.updateCustomersOverdraftStatus
        .matchFulfilled,
      (
        state,
        action: PayloadAction<{
          data: Account & { overdraftEnabled?: boolean };
        }>,
      ) => {
        if (state.baseInformation) {
          const nextBaseInformation = { ...state.baseInformation };

          if (nextBaseInformation.accounts) {
            const nextAccounts = nextBaseInformation.accounts?.map((item) => {
              const result = { ...item };

              if (result.id === action.payload.data.id) {
                result.overdraftEnabled = action.payload.data.overdraftEnabled;
              }

              return result;
            }, []);

            nextBaseInformation.accounts = nextAccounts;

            state.baseInformation = nextBaseInformation;
          }
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.closeOrganization.matchFulfilled,
      (state, action: PayloadAction<{ data: CustomerProps }>) => {
        if (state.baseInformation) {
          const { data } = action.payload;

          // @ts-ignore
          state.baseInformation = {
            ...state.baseInformation,
            ...data,
          };
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.reopenOrganization.matchFulfilled,
      (state, action: PayloadAction<{ data: CustomerProps }>) => {
        if (state.baseInformation) {
          const { data } = action.payload;

          // @ts-ignore
          state.baseInformation = {
            ...state.baseInformation,
            ...data,
          };
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.updateCustomersOverdraftBalanceValue
        .matchFulfilled,
      (state, action: PayloadAction<{ data: Balance }>) => {
        state.balances = state.balances?.map((item) => {
          let result = { ...item };

          if (result.id === action.payload.data.id) {
            result = action.payload.data;
          }

          return result;
        });
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.assignTariffToCustomer.matchFulfilled,
      (state, action: PayloadAction<{ data: CustomerProps }>) => {
        const { data } = action.payload;

        const fees: FeeItem[] = [];

        data.customFees?.forEach((item) => {
          fees.push({
            ...item,
            currency: 'GBP',
            writeOffValue: '-',
            isModified: true,
          });
        });

        data.tariff?.fees.forEach((item) => {
          if (!fees.find((fee) => fee.feeId === item.feeId)) {
            fees.push({
              ...item,
              currency: 'GBP',
              writeOffValue: '-',
            });
          }
        });

        state.baseInformation = {
          ...state.baseInformation,
          tariff: data?.tariff ? data.tariff.name : 'No Tariff',
          tariffId: data?.tariff?.id,
          defaultFees: data?.tariff?.fees.map((item) => ({
            ...item,
            currency: 'GBP',
            writeOffValue: '-',
          })),
          fees,
        };
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.updateCustomerRanges.matchFulfilled,
      (state, action: PayloadAction<{ data: CustomerProps }>) => {
        if (state.baseInformation) {
          const { data } = action.payload;

          state.baseInformation = {
            ...state.baseInformation,
            monthlyTurnoverRangeId: data.monthlyTurnoverRangeId,
            monthlyNumberIncomingTransactionsRangeId:
              data.monthlyNumberIncomingTransactionsRangeId,
            monthlyNumberOutgoingTransactionsRangeId:
              data.monthlyNumberOutgoingTransactionsRangeId,
            monthlyValueIncomingTransactionsRangeId:
              data.monthlyValueIncomingTransactionsRangeId,
            monthlyValueOutgoingTransactionsRangeId:
              data.monthlyValueOutgoingTransactionsRangeId,
            monthlyMaxValueIncomingPaymentRangeId:
              data.monthlyMaxValueIncomingPaymentRangeId,
            monthlyMaxValueOutgoingPaymentRangeId:
              data.monthlyMaxValueOutgoingPaymentRangeId,
          };
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.blockCustomerAccountCredit.matchFulfilled,
      (state, action: PayloadAction<{ data: Account }>) => {
        const { data } = action.payload;

        const nextBaseInformation = { ...state.baseInformation };
        const nextAccounts = nextBaseInformation.accounts?.map((item) => {
          const result = { ...item };

          if (result.id === data.id) {
            result.creditBlock = data.creditBlock;
          }

          return result;
        });

        nextBaseInformation.accounts = nextAccounts;

        state.baseInformation = nextBaseInformation;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.updateCustomerAccountVisibility
        .matchFulfilled,
      (
        state,
        action: PayloadAction<{ data: Account & { visibleToClient: boolean } }>,
      ) => {
        const { data } = action.payload;

        const nextBaseInformation = { ...state.baseInformation };
        const nextAccounts = nextBaseInformation.accounts?.map((item) => {
          const result = { ...item };

          if (result.id === data.id) {
            result.visibleToClient = data.visibleToClient;
          }

          return result;
        });

        nextBaseInformation.accounts = nextAccounts;

        state.baseInformation = nextBaseInformation;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.unblockCustomerAccountCredit
        .matchFulfilled,
      (state, action: PayloadAction<{ data: Account }>) => {
        const { data } = action.payload;

        const nextBaseInformation = { ...state.baseInformation };
        const nextAccounts = nextBaseInformation.accounts?.map((item) => {
          const result = { ...item };

          if (result.id === data.id) {
            result.creditBlock = data.creditBlock;
          }

          return result;
        });

        nextBaseInformation.accounts = nextAccounts;

        state.baseInformation = nextBaseInformation;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.closeCustomerAccount.matchFulfilled,
      (state, action: PayloadAction<{ data: Account }>) => {
        const { data } = action.payload;

        const nextBaseInformation = { ...state.baseInformation };
        const nextAccounts = nextBaseInformation.accounts?.map((item) => {
          const result = { ...item };

          if (result.id === data.id) {
            result.closed = data.closed;
          }

          return result;
        });

        nextBaseInformation.accounts = nextAccounts;

        state.baseInformation = nextBaseInformation;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.openCustomerAccount.matchFulfilled,
      (state, action: PayloadAction<{ data: Account }>) => {
        const { data } = action.payload;

        const nextBaseInformation = { ...state.baseInformation };
        const nextAccounts = nextBaseInformation.accounts?.map((item) => {
          const result = { ...item };

          if (result.id === data.id) {
            result.closed = data.closed;
          }

          return result;
        });

        nextBaseInformation.accounts = nextAccounts;

        state.baseInformation = nextBaseInformation;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.blockCustomerAccountDebit.matchFulfilled,
      (state, action: PayloadAction<{ data: Account }>) => {
        const { data } = action.payload;

        const nextBaseInformation = { ...state.baseInformation };
        const nextAccounts = nextBaseInformation.accounts?.map((item) => {
          const result = { ...item };

          if (result.id === data.id) {
            result.debitBlock = data.debitBlock;
          }

          return result;
        });

        nextBaseInformation.accounts = nextAccounts;

        state.baseInformation = nextBaseInformation;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.unblockCustomerAccountDebit
        .matchFulfilled,
      (state, action: PayloadAction<{ data: Account }>) => {
        const { data } = action.payload;

        const nextBaseInformation = { ...state.baseInformation };
        const nextAccounts = nextBaseInformation.accounts?.map((item) => {
          const result = { ...item };

          if (result.id === data.id) {
            result.debitBlock = data.debitBlock;
          }

          return result;
        });

        nextBaseInformation.accounts = nextAccounts;

        state.baseInformation = nextBaseInformation;
      },
    );
    builder
      .addMatcher(
        corporateCustomersApi.endpoints.getCorporateMainChannel.matchFulfilled,
        (
          state,
          action: PayloadAction<
            Response<{
              id: string;
            }>
          >,
        ) => {
          if (
            action?.payload?.data?.edges &&
            action?.payload?.data?.edges.length &&
            action?.payload?.data?.edges[0]
          ) {
            state.mainChat = action?.payload?.data?.edges[0].node;
          }
        },
      )
      .addMatcher(
        customersComponentApi.endpoints.getCustomerComments.matchFulfilled,
        (
          state,
          action: PayloadAction<Response<DocumentComment>> & {
            meta: {
              arg: {
                originalArgs: {
                  cursor?: string;
                };
              };
            };
          },
        ) => {
          let comments;

          if (!action.meta?.arg?.originalArgs?.cursor) {
            comments = action.payload.data.edges?.map((item) => item.node);
          } else {
            comments = ([] as DocumentComment[]).concat(
              state.customerComments as DocumentComment[],
            );

            action.payload.data.edges?.forEach((item) =>
              comments.push(item.node),
            );
          }

          state.customerComments = comments;
          state.customerCommentsPagination = action.payload.data.pageInfo;
        },
      )
      .addMatcher(
        customersComponentApi.endpoints.addCommentToCustomer.matchFulfilled,
        (state, action: PayloadAction<{ data: DocumentComment }>) => {
          let comments: DocumentComment[] = [];

          if (state.customerComments) {
            comments = ([] as DocumentComment[]).concat(state.customerComments);
          }

          comments.unshift(action.payload.data);

          state.customerComments = comments;

          if (state.customerCommentsPagination) {
            state.customerCommentsPagination = {
              ...state.changeLogsPagination,
              totalCount:
                (state.customerCommentsPagination?.totalCount || 0) + 1,
            } as Pagination;
          }
        },
      )
      .addMatcher(
        customersComponentApi.endpoints.editCommentInCustomer.matchFulfilled,
        (state, action: PayloadAction<{ data: DocumentComment }>) => {
          state.customerComments = state.customerComments?.map((item) => {
            const result = { ...item };

            if (result.id === item.id) {
              result.text = action.payload.data.text;
            }

            return result;
          });
        },
      )
      .addMatcher(
        customersComponentApi.endpoints.deleteCommentInCustomer.matchFulfilled,
        (state, action: PayloadAction<{ data: DocumentComment }>) => {
          const nextTranscationComments = state?.customerComments?.filter(
            (item) => item.id !== action.payload.data.id,
          );

          state.customerComments = nextTranscationComments;

          if (state.customerCommentsPagination) {
            state.customerCommentsPagination = {
              ...state.changeLogsPagination,
              totalCount:
                (state.customerCommentsPagination?.totalCount || 0) - 1,
            } as Pagination;
          }
        },
      );
  },
});

export const { clearGeneralState, setBalances } =
  CorporateCustomerGeneralInfoSlice.actions;

export default CorporateCustomerGeneralInfoSlice.reducer;
